/**
 * LIGHTNING ⚡ ARENA — Lightning Yard
 * Quake 3 Arena style FFA deathmatch in the browser, Bitcoin edition.
 * Player + 5 legend bots, LG/RL/RG/SG, jump pads, teleporters, items, quad,
 * frag limit 20 / 10 min, sats bounty per frag, Lightning/Nostr in background.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';

import { createSpaceEnvironment, configureTextures } from '../render/materials';
import { FX } from '../render/fx';
import { LightningYard, ItemSpawn } from './map';
import { QuakePlayer, PM } from './movement';
import { WEAPONS, WEAPON_ORDER, WeaponId, Hittable, trace, traceBlocked, LightningBolt, Rocket } from './weapons';
import { Bot, LEGENDS, LegendDef, resetPlayerFocus } from './bots';
import { HUD, ScoreRow } from './hud';
import { Sound } from './sound';
import { ViewWeapon } from './viewweapon';
import { ArenaClient, ArenaJoin } from '../core/arena';
import { LeaderboardClient, LeaderboardPeriod } from '../core/leaderboard';
import { NostrClient } from '../core/nostr';
import { loadNickname, sanitizeNickname } from '../core/nickname';

const FRAG_LIMIT = 20;
const TIME_LIMIT = 10 * 60;
const BOUNTY_SATS = 600;
const ENTRY_SATS = 2100;
const MAX_PAID_FRAGS = 3;
const RESPAWN_MS = 2500;

class PlayerEntity implements Hittable {
  id = 'player';
  mover = new QuakePlayer();
  health = 100; armor = 0;
  alive_ = true;
  frags = 0; deaths = 0; sats = 0;
  quadUntil = 0;
  ammo: Record<WeaponId, number> = { lightning: WEAPONS.lightning.ammoStart, rocket: WEAPONS.rocket.ammoStart, rail: WEAPONS.rail.ammoStart, shotgun: WEAPONS.shotgun.ammoStart };
  weapon: WeaponId = 'lightning';
  alive(): boolean { return this.alive_; }
  hitBox(out: { min: THREE.Vector3; max: THREE.Vector3 }): void {
    const p = this.mover.position;
    out.min.set(p.x - PM.playerRadius, p.y, p.z - PM.playerRadius);
    out.max.set(p.x + PM.playerRadius, p.y + this.mover.height, p.z + PM.playerRadius);
  }
}

export class Game {
  private renderer!: THREE.WebGLRenderer;
  private composer!: EffectComposer;
  private fxaa!: ShaderPass;
  private softRaster = false;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(100, 1, 0.05, 2000);
  private clock = new THREE.Clock();
  private map!: LightningYard;
  private hud = new HUD();
  private sound = new Sound();
  private view!: ViewWeapon;
  private fx!: FX;

  private player = new PlayerEntity();
  private bots: Bot[] = [];
  private rockets: Rocket[] = [];
  private lgBolt!: LightningBolt;
  private botBolts = new Map<string, LightningBolt>();

  private keys = new Set<string>();
  private mouseDX = 0; private mouseDY = 0;
  private firing = false;
  private fireCooldown = 0;
  private lgWasFiring = false;
  private railWhined = true;
  private started = false;
  private over = false;
  private matchStart = 0;
  private lastLeader = '';
  private playerLegend: LegendDef = LEGENDS[0];
  /** Pseudo saisi au menu — nom d'affichage local (killfeed, scoreboard, annonces). */
  private playerName = loadNickname();
  /** Deux accumulateurs distincts : la régénération santé (1 s) et les bruits de
   *  pas (fonction de la vitesse) partageaient la même variable et se remettaient
   *  mutuellement à zéro (pas de régén en courant, pas fantômes à l'arrêt). */
  private healthStepAcc = 0;
  private footstepAcc = 0;
  private scoreboardHeld = false;
  /** Scoreboard : reconstruit uniquement sur changement (frag, mort, Tab). */
  private scoreboardShown = false;
  private scoreSigShown = -1;

  // caméra : secousse, zoom, adaptation de résolution
  private shake = 0;
  private zooming = false;
  private fovTarget = 100;
  private onDeck = false;
  private aimAssist = 1;
  private aimOnEnemy = false;
  private perfAcc = 0; private perfFrames = 0; private perfAt = performance.now();
  private readonly isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  private pixelRatioNow = Math.min(window.devicePixelRatio, 1.75);
  private blockTimer = 0;

  private arena = new ArenaClient();
  private leaderboard = new LeaderboardClient(this.arena.baseUrl());
  private nostr: NostrClient | null = null;
  private arenaSession: ArenaJoin | null = null;
  private arenaCredit = 0;
  private arenaPool = 0;
  private arenaPaid = false;
  private matchId = '';

  /** cache de ligne de vue : une trace par paire toutes les ~3 frames */
  private losCache = new Map<number, number>();
  private losFrame = 0;
  private lastPainAt = 0;
  private lastArcAt = 0;
  private selfSplash = false;
  private readonly SESSION_KEY = 'lightning-arena.session';

  private tmpA = new THREE.Vector3(); private tmpB = new THREE.Vector3(); private tmpC = new THREE.Vector3();
  private tmpItem = new THREE.Vector3(); private tmpLos = new THREE.Vector3();
  private vRight = new THREE.Vector3(); private vRight2 = new THREE.Vector3();
  private vUp = new THREE.Vector3(); private vTmp = new THREE.Vector3();
  private readonly vUpUnit = new THREE.Vector3(0, 1, 0);
  // temporaires de la boucle roquettes (position précédente, direction, point
  // d'explosion) + liste de cibles réutilisée : la boucle tournait avec 3
  // Vector3 et 2 tableaux neufs par roquette et par frame.
  private rkPrev = new THREE.Vector3(); private rkDir = new THREE.Vector3(); private rkBoom = new THREE.Vector3();
  private vKnock = new THREE.Vector3();
  private rocketTargets: Hittable[] = [];
  /** vue « joueur » passée aux bots — un objet réutilisé au lieu d'un par bot/frame */
  private botPlayerView = { position: this.player.mover.position, alive: true, eye: this.tmpLos };
  /** callback de ligne de vue liée une fois pour toutes (plus de closure par frame) */
  private readonly canSeeFn = (from: THREE.Vector3, to: THREE.Vector3) => this.canSeeCached(from, to);

  constructor() {
    this.initRenderer();
    this.initWorld();
    this.initInput();
    this.hud.setTouchVisible(false);
    this.hud.showMenu(LEGENDS.filter(l => l.id !== 'lagarde').map(l => ({ id: l.id, name: l.name, title: l.title, accent: l.accent })), this.hud.touch.active);
    this.hud.onStart = (legendId, name) => this.start(legendId, name);
    this.hud.onNextWeapon = () => this.cycleWeapon(1);
    this.hud.onJoinArena = () => { void this.joinArena(); };
    this.hud.onWithdraw = () => { void this.withdrawArena(); };
    this.hud.onCancelJoin = () => { this.arenaSession = null; this.clearSession(); this.hud.setLeaderboardIdentity(null); };
    this.hud.onBackToMenu = () => this.backToMenu();
    // Classement : lecture publique (aucune session requise pour consulter).
    this.hud.onLoadLeaderboard = (period) => this.leaderboard.topKillers(period, 10);
    this.hud.onLoadMyRank = (period) => this.myLeaderboardRank(period);
    this.initNetwork();
    this.renderer.setAnimationLoop(() => this.frame());
  }

  // ---------------------------------------------------------------- init

  private initRenderer(): void {
    const canvas = document.createElement('canvas');
    canvas.id = 'game-canvas';
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;display:block;touch-action:none;';
    document.body.appendChild(canvas);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', stencil: false });
    this.renderer.setPixelRatio(this.pixelRatioNow);
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Ombres désactivées sur tactile : ~2× de draw calls pour un gain visuel
    // marginal sur un écran de téléphone.
    this.renderer.shadowMap.enabled = !this.isTouch;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    configureTextures(this.renderer);

    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    // Software rasterizer (headless tests, weak machines) → skip the post
    // pipeline: bloom/FXAA would run at <1 fps in CPU WebGL.
    const gl = this.renderer.getContext();
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const rendererName = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string : '';
    this.softRaster = /swiftshader|llvmpipe|software/i.test(rendererName);

    const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    if (!this.softRaster) {
      this.composer = new EffectComposer(this.renderer, new THREE.WebGLRenderTarget(size.x, size.y, { samples: 0, type: THREE.HalfFloatType }));
      this.composer.addPass(new RenderPass(this.scene, this.camera));
      // UnrealBloomPass divise déjà la résolution en interne : lui passer une
      // taille déjà divisée donnait un quart de résolution, puis composer.setSize
      // la remettait à la moitié au premier resize (4× plus cher).
      const bloom = new UnrealBloomPass(new THREE.Vector2(size.x, size.y), 0.55, 0.6, 0.82);
      this.composer.addPass(bloom);
      this.fxaa = new ShaderPass(FXAAShader);
      (this.fxaa.material.uniforms['resolution'].value as THREE.Vector2).set(1 / size.x, 1 / size.y);
      this.composer.addPass(this.fxaa);
      this.composer.addPass(new OutputPass());
    }

    window.addEventListener('resize', () => {
      this.renderer.setSize(window.innerWidth, window.innerHeight, false);
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      if (!this.softRaster) {
        const s = this.renderer.getDrawingBufferSize(new THREE.Vector2());
        this.composer.setSize(s.x, s.y);
        (this.fxaa.material.uniforms['resolution'].value as THREE.Vector2).set(1 / s.x, 1 / s.y);
      }
    });
  }

  private initWorld(): void {
    const { sky, envMap } = createSpaceEnvironment(this.renderer);
    this.scene.add(sky);
    this.scene.environment = envMap;
    this.scene.fog = new THREE.FogExp2(0x06040c, 0.0055);

    // Lighting: warm key (Bitcoin sun), cool fill from the nebula, hemisphere
    const key = new THREE.DirectionalLight(0xffd9a8, 2.6);
    key.position.set(60, 90, 40);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 10; key.shadow.camera.far = 250;
    key.shadow.camera.left = -70; key.shadow.camera.right = 70; key.shadow.camera.top = 70; key.shadow.camera.bottom = -70;
    key.shadow.bias = -0.0005; key.shadow.normalBias = 0.03;
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0x4fa0ff, 0.6);
    fill.position.set(-60, 30, -50);
    this.scene.add(fill);
    this.scene.add(new THREE.HemisphereLight(0x3a4a7a, 0x120a04, 0.55));

    this.map = new LightningYard();
    this.scene.add(this.map.group);

    // Moteur d'effets poolé — toutes les particules/flashes passent par lui
    this.fx = new FX(this.scene, { touch: this.isTouch });
    this.fx.setFloorQuery((x, y, z) => this.map.floorAt(x, y, z));
    this.fx.ambientMotes(new THREE.Vector3(0, 6, 0), 44, 90);

    this.lgBolt = new LightningBolt(this.scene, WEAPONS.lightning.color);
    this.view = new ViewWeapon(this.camera, this.playerLegend.accent);

    // Bots (5 opponents; Lagarde joins as the boss)
    for (const def of [LEGENDS[1], LEGENDS[2], LEGENDS[3], LEGENDS[5], LEGENDS[7]]) {
      const bot = new Bot(def, this.scene);
      const avoids = [this.player.mover.position, ...this.bots.map(b => b.mover.position)];
      bot.spawn(this.map, avoids);
      this.bots.push(bot);
      this.botBolts.set(bot.id, new LightningBolt(this.scene, def.accent));
    }

    // Player initial camera on a spawn (menu background view)
    const s = this.map.spawns[0];
    this.player.mover.teleport(s.position, s.yaw);
    this.player.mover.pitch = -0.15;
  }

  private initInput(): void {
    const canvas = this.renderer.domElement;
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'Tab') { e.preventDefault(); if (!this.scoreboardHeld) { this.scoreboardHeld = true; this.refreshScoreboard(); } }
      if (e.code >= 'Digit1' && e.code <= 'Digit4') this.selectWeapon(WEAPON_ORDER[parseInt(e.code[5]) - 1]);
      if (e.code === 'KeyQ') this.cycleWeapon(-1);
      if (e.code === 'KeyE') this.cycleWeapon(1);
    });
    window.addEventListener('keyup', (e) => { this.keys.delete(e.code); if (e.code === 'Tab') { this.scoreboardHeld = false; this.refreshScoreboard(); } });
    window.addEventListener('wheel', (e) => this.cycleWeapon(e.deltaY > 0 ? 1 : -1));
    canvas.addEventListener('mousedown', (e) => { if (e.button === 0) { this.firing = true; if (this.started && document.pointerLockElement !== canvas) canvas.requestPointerLock(); } });
    window.addEventListener('mouseup', (e) => { if (e.button === 0) this.firing = false; });
    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement === canvas) { this.mouseDX += e.movementX; this.mouseDY += e.movementY; }
    });
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement !== canvas && this.started && !this.over && !this.hud.touch.active) {
        this.hud.frag('CLICK TO RESUME', 'pointer lock released');
      }
    });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    // Zoom = clic droit maintenu (hors menus)
    canvas.addEventListener('mousedown', (e) => { if (e.button === 2) this.zooming = true; });
    window.addEventListener('mouseup', (e) => { if (e.button === 2) this.zooming = false; });
    window.addEventListener('blur', () => {
      // sinon une touche restée « enfoncée » fait courir le joueur tout seul
      this.zooming = false; this.firing = false;
      this.keys.clear();
      this.scoreboardHeld = false;
      this.refreshScoreboard();
    });
  }

  private initNetwork(): void {
    // Non-blocking: game runs even offline; real sats need the arena API
    this.arena.probe().then((ok) => {
      this.hud.setNet(ok ? 'LIGHTNING' : 'OFFLINE · DEMO SATS', ok);
      this.hud.setArenaAvailable(ok);
      if (ok) {
        this.hud.setArenaEconomy(this.arena.entrySats, this.arena.bountySats, this.arena.maxFrags);
        void this.restoreSession();
      }
    });
    void this.fetchBlockHeight();
    try {
      this.nostr = new NostrClient({ relays: ['wss://relay.damus.io', 'wss://nos.lol'], autoConnect: true });
      this.nostr.connect().catch(() => {});
    } catch { /* ignore */ }
  }

  /** Hauteur de bloc en direct (mempool.space) → hologramme + panneaux data. */
  private async fetchBlockHeight(): Promise<void> {
    try {
      const r = await fetch('https://mempool.space/api/blocks/tip/height', { cache: 'no-store' });
      const h = parseInt(await r.text(), 10);
      if (Number.isFinite(h)) this.map.setBlockHeight(h);
    } catch { /* silencieux : le jeu tourne hors ligne */ }
  }

  /**
   * Angle écran d'un point par rapport à la visée (0 = devant, +π/2 = à droite).
   * Utilisé par l'indicateur directionnel de dégâts du HUD.
   */
  private screenAngleTo(at: THREE.Vector3): number {
    const d = this.vTmp.subVectors(at, this.player.mover.position);
    const f = this.player.mover.forward(this.tmpB);
    const r = this.camRight(this.vRight);
    return Math.atan2(d.x * r.x + d.z * r.z, d.x * f.x + d.z * f.z);
  }

  // ---------------------------------------------------------------- arena economy

  /** Paid entry flow: create invoice, show QR + lightning: link, poll until paid. */
  private async joinArena(): Promise<void> {
    try {
      const session = await this.arena.join();
      this.arenaSession = session;
      this.matchId = crypto.randomUUID();
      this.saveSession();
      await this.hud.showJoinPayment(session, this.arena.invoiceQrUrl(session.session_token));
      // Poll for payment
      const poll = setInterval(async () => {
        if (!this.arenaSession) { clearInterval(poll); return; }
        try {
          const st = await this.arena.status(this.arenaSession.session_token);
          if (st.paid) {
            clearInterval(poll);
            this.arenaPaid = true;
            this.arenaCredit = st.credit;
            this.arenaPool = st.pool;
            this.saveSession();
            this.syncLeaderboard();
            this.hud.setArenaPaid(true, st.pool);
            this.startMatchAfterPayment();
          }
        } catch { /* retry */ }
      }, 2000);
      this.hud.pollStop = () => clearInterval(poll);
    } catch (e) {
      this.hud.frag('ARENA OFFLINE', e instanceof Error ? e.message.slice(0, 60) : 'réseau');
    }
  }

  /**
   * Persistance de la session Lightning payée. Sans elle, un PLAY AGAIN ou un
   * retour au menu (qui rechargeaient la page) faisait perdre l'entrée payée
   * ET le crédit bounty non retiré.
   */
  private saveSession(): void {
    if (!this.arenaSession) return;
    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify({
        session: this.arenaSession,
        matchId: this.matchId,
        credit: this.arenaCredit,
        paid: this.arenaPaid,
      }));
    } catch { /* quota plein / navigation privée : la session reste en mémoire */ }
  }

  private clearSession(): void {
    try { localStorage.removeItem(this.SESSION_KEY); } catch { /* ignore */ }
  }

  /**
   * Au démarrage : si un token payé traîne en localStorage, on le revalide côté
   * serveur et on réactive le bounty réel. Crédit non retiré → écran de retrait
   * proposé directement, sans relancer de match.
   */
  private async restoreSession(): Promise<void> {
    let raw: string | null = null;
    try { raw = localStorage.getItem(this.SESSION_KEY); } catch { return; }
    if (!raw) return;
    let d: { session?: ArenaJoin; matchId?: string; paid?: boolean };
    try { d = JSON.parse(raw); } catch { this.clearSession(); return; }
    const token = d?.session?.session_token;
    if (!token) { this.clearSession(); return; }
    try {
      const st = await this.arena.status(token);
      if (!st.paid) { this.clearSession(); return; }
      this.arenaSession = d.session!;
      this.matchId = d.matchId || crypto.randomUUID();
      this.arenaPaid = true;
      this.arenaCredit = st.credit;
      this.arenaPool = st.pool;
      this.saveSession();
      this.hud.setWithdrawQrUrl(this.arena.withdrawQrUrl(token));
      this.syncLeaderboard();
      this.hud.setArenaRestored(st.credit, st.pool);
      if (st.credit > 0 && !this.started) this.hud.showPendingCredit(st.credit, st.pool);
    } catch { /* API injoignable : on garde la session pour un prochain essai */ }
  }

  /**
   * Classement : inscrit la session PAYÉE (le worker revérifie le token auprès
   * du serveur Lightning et n'en persiste que le sha256) et arme le surlignage
   * de la ligne du joueur. Fire & forget — worker absent ou non déployé, le jeu
   * n'en sait rien et continue.
   */
  private syncLeaderboard(): void {
    const s = this.arenaSession;
    if (!s || !this.arenaPaid) return;
    this.hud.setLeaderboardIdentity(this.leaderboard.playerId(s.session_token));
    void this.leaderboard.registerSession(s.session_token, this.hud.getNickname());
  }

  /** Rang général du joueur local (0 = non classé, ou pas de session payée). */
  private async myLeaderboardRank(period: LeaderboardPeriod): Promise<number> {
    const s = this.arenaSession;
    if (!s || !this.arenaPaid) return 0;
    return this.leaderboard.rank(this.leaderboard.playerId(s.session_token), period);
  }

  /** Retour au menu sans recharger la page (la session payée est conservée). */
  private backToMenu(): void {
    this.over = false;
    this.started = false;
    this.lgOff();
    for (const r of this.rockets) r.dispose(this.scene);
    this.rockets.length = 0;
    this.player.frags = 0; this.player.deaths = 0; this.player.sats = 0;
    this.lastLeader = '';
    for (const b of this.bots) { b.frags = 0; b.deaths = 0; b.spawn(this.map, this.aliveAvoids()); }
    this.hud.setTouchVisible(false);
    this.hud.showMenu(LEGENDS.filter(l => l.id !== 'lagarde').map(l => ({ id: l.id, name: l.name, title: l.title, accent: l.accent })), this.hud.touch.active);
    this.hud.setArenaAvailable(this.arena.available);
    if (this.arena.available) this.hud.setArenaEconomy(this.arena.entrySats, this.arena.bountySats, this.arena.maxFrags);
    if (this.arenaPaid) {
      this.hud.setArenaRestored(this.arenaCredit, this.arenaPool);
      if (this.arenaCredit > 0) this.hud.showPendingCredit(this.arenaCredit, this.arenaPool);
    }
  }

  /** Paid players enter the real match; demo players can fight for free. */
  private startMatchAfterPayment(): void {
    if (!this.started && !this.over) {
      // Reuse the currently selected legend: restart menu is simpler — direct FIGHT
      this.hud.startFight();
    }
  }

  /** Report a frag to the arena treasury (fire & forget). */
  private reportFrag(): void {
    const s = this.arenaSession;
    if (!s || !this.arenaPaid) return;
    this.arena.kill(s.session_token, this.matchId).then((r) => {
      this.arenaCredit = r.credit;
      this.arenaPool = r.pool;
      this.saveSession();
    }).catch((e) => {
      console.warn('[Arena] kill report failed:', e.message);
    });
  }

  /** Withdraw credit: create an LNURL-withdraw link, show the QR, poll until claimed. */
  private async withdrawArena(): Promise<void> {
    const s = this.arenaSession;
    if (!s || !this.arenaPaid) throw new Error('Pas de session payée');
    try {
      const r = await this.arena.withdraw(s.session_token);
      this.arenaCredit = r.credit;
      this.arenaPool = r.pool;
      this.hud.setWithdrawQrUrl(this.arena.withdrawQrUrl(s.session_token));
      this.hud.showWithdrawQr(r.amount_sats);
      // Poll claim status every 2.5 s while the end screen is up
      const poll = setInterval(async () => {
        try {
          const st = await this.arena.withdrawStatus(s.session_token);
          if (st.claimed) {
            clearInterval(poll);
            this.arenaCredit = st.credit;
            this.arenaPool = st.pool;
            // retrait encaissé et solde à zéro : la session n'a plus rien à porter
            if (st.credit <= 0) this.clearSession(); else this.saveSession();
            this.hud.showWithdrawClaimed();
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      this.hud.showWithdrawResult(false, e instanceof Error ? e.message : 'échec');
    }
  }

  // ---------------------------------------------------------------- match

  private start(legendId: string, name?: string): void {
    this.playerLegend = LEGENDS.find(l => l.id === legendId) ?? LEGENDS[0];
    this.playerName = sanitizeNickname(name ?? this.playerName);
    this.sound.unlock();
    this.started = true;
    this.matchStart = performance.now();
    this.respawnPlayer();
    this.hud.setTouchVisible(true);
    if (!this.hud.touch.active) this.renderer.domElement.requestPointerLock();
    this.hud.frag('FIGHT!', `${this.playerName.toUpperCase()} ENTERS THE YARD · ${this.playerLegend.name.toUpperCase()}`);
    this.sound.announce('fight');
    this.view.setAccent(this.playerLegend.accent);
    this.view.select(this.player.weapon);
  }

  private respawnPlayer(): void {
    // évite de réapparaître dans les pieds d'un adversaire vivant
    const s = this.map.randomSpawn(this.aliveAvoids());
    this.player.mover.teleport(s.position, s.yaw);
    this.player.mover.pitch = 0;
    this.player.health = 125; // Q3 spawn health decays to 100
    this.player.armor = 0;
    this.player.alive_ = true;
    this.player.ammo = { lightning: WEAPONS.lightning.ammoStart, rocket: WEAPONS.rocket.ammoStart, rail: WEAPONS.rail.ammoStart, shotgun: WEAPONS.shotgun.ammoStart };
    this.player.weapon = 'lightning';
    this.onDeck = false;
    // le zoom tactile ne doit pas survivre à la mort
    this.zooming = false;
    this.hud.touch.zoom = false;
    this.hud.setTouchZoomOff();
    this.hud.setZooming(false);
    this.view.select('lightning');
  }

  private endMatch(): void {
    if (this.over) return;
    this.over = true;
    this.lgOff();
    // roquettes en vol : leur slot de lumière FX doit être rendu, sinon le pool
    // de 6 PointLights se vide match après match
    for (const r of this.rockets) r.dispose(this.scene);
    this.rockets.length = 0;
    for (const b of this.botBolts.values()) b.hide();
    this.zooming = false;
    this.hud.touch.zoom = false;
    this.hud.setTouchZoomOff();
    this.hud.setZooming(false);
    this.camera.fov = 100; this.camera.updateProjectionMatrix();
    document.exitPointerLock?.();
    this.hud.setTouchVisible(false);
    const rows = this.scoreRows();
    const won = rows.sort((a, b) => b.frags - a.frags)[0].me === true;
    this.sound.announce(won ? 'win' : 'lost');
    // les frags du match viennent d'être crédités côté serveur Lightning : on
    // repousse la session au classement pour qu'il en enregistre le delta
    this.syncLeaderboard();
    const arenaState = this.arenaPaid ? { paid: true, credit: this.arenaCredit, pool: this.arenaPool } : { paid: false, credit: 0, pool: this.arenaPool };
    setTimeout(() => this.hud.showMenuEnd(rows, won, this.player.sats, arenaState), 800);
  }

  private scoreRows(): ScoreRow[] {
    const rows: ScoreRow[] = [{ name: this.playerName, legend: this.playerLegend.name, frags: this.player.frags, deaths: this.player.deaths, sats: this.player.sats, me: true, color: '#' + this.playerLegend.accent.toString(16).padStart(6, '0') }];
    for (const b of this.bots) rows.push({ name: b.def.name, frags: b.frags, deaths: b.deaths, sats: b.frags * BOUNTY_SATS, color: '#' + b.def.accent.toString(16).padStart(6, '0') });
    return rows;
  }

  /**
   * Pousse le scoreboard vers le HUD UNIQUEMENT quand quelque chose a changé :
   * frag/mort (scoreDirty) ou ouverture/fermeture (Tab). Auparavant le tableau
   * était reconstruit — 6 objets, un tri et un innerHTML — à chaque frame.
   */
  private refreshScoreboard(): void {
    if (this.scoreboardHeld && this.started && !this.over) {
      const sig = this.scoreSig();
      if (this.scoreboardShown && sig === this.scoreSigShown) return;
      this.hud.showScoreboard(this.scoreRows());
      this.scoreboardShown = true;
      this.scoreSigShown = sig;
    } else if (this.scoreboardShown) {
      this.hud.showScoreboard(null);
      this.scoreboardShown = false;
    }
  }

  /**
   * Signature de l'état affiché (frags / morts / sats), sans allocation.
   * Pendant un match ces compteurs ne font que croître et chaque champ a un
   * poids strictement positif : toute évolution change la somme. Couvre donc
   * les frags du joueur, ceux des bots, les morts (y compris les chutes dans
   * le vide côté IA) et les sats ramassés.
   */
  private scoreSig(): number {
    let s = this.player.frags * 3 + this.player.deaths * 5 + this.player.sats * 7;
    for (let i = 0; i < this.bots.length; i++) {
      const b = this.bots[i];
      s += (i + 2) * (b.frags * 11 + b.deaths * 13);
    }
    return s;
  }

  /**
   * Classement en tête sans allouer : plus de `scoreRows().sort()` par frame
   * (6 objets + un tableau + un tri à 60 fps pour une seule ligne de HUD).
   * Les égalités se résolvent comme le tri stable précédent : le joueur d'abord.
   */
  private leaderText(): string {
    let topFrags = this.player.frags, topName = this.playerName, topIsMe = true, second = -1;
    for (const b of this.bots) {
      if (b.frags > topFrags) { second = topFrags; topFrags = b.frags; topName = b.def.name; topIsMe = false; }
      else if (b.frags > second) second = b.frags;
    }
    if (second < 0) second = 0;
    if (topIsMe) return second === topFrags ? 'TIED' : `YOU +${topFrags - second}`;
    return `${topName.split(' ').pop()} ${topFrags}`;
  }

  // ---------------------------------------------------------------- weapons

  private selectWeapon(w: WeaponId): void {
    if (!this.started || this.player.weapon === w) return;
    this.lgOff();
    this.player.weapon = w;
    this.view.select(w);
  }

  private cycleWeapon(dir: number): void {
    const i = WEAPON_ORDER.indexOf(this.player.weapon);
    for (let k = 1; k <= WEAPON_ORDER.length; k++) {
      const w = WEAPON_ORDER[(i + dir * k + WEAPON_ORDER.length * 4) % WEAPON_ORDER.length];
      if (this.player.ammo[w] > 0) { this.selectWeapon(w); return; }
    }
  }

  private lgOff(): void {
    if (this.lgWasFiring) { this.sound.lightningStop(); this.lgWasFiring = false; }
    this.lgBolt.hide();
  }

  private playerFire(dt: number): void {
    const p = this.player;
    const wantFire = (this.firing || this.hud.touch.fire) && p.alive() && this.started && !this.over;
    const def = WEAPONS[p.weapon];
    this.fireCooldown -= dt;

    if (!wantFire || p.ammo[p.weapon] <= 0) {
      if (p.weapon === 'lightning') this.lgOff();
      if (wantFire && p.ammo[p.weapon] <= 0) this.cycleWeapon(1);
      return;
    }
    if (this.fireCooldown > 0) {
      // keep LG visual continuous between ticks
      if (p.weapon === 'lightning' && this.lgWasFiring) this.updateLGBolt();
      // le railgun siffle en fin de recharge (cascade d'anneaux côté arme)
      if (p.weapon === 'rail' && !this.railWhined && this.fireCooldown < 0.55) {
        this.railWhined = true;
        this.sound.railCharge();
      }
      return;
    }
    this.fireCooldown = def.fireInterval;
    if (p.weapon === 'rail') this.railWhined = false;
    const quad = performance.now() < p.quadUntil ? 3 : 1;

    const origin = p.mover.eye(this.tmpA).clone();
    const dir = p.mover.forward(this.tmpB).clone();
    const targets: Hittable[] = this.bots;
    // base caméra pour orienter les flashs de bouche (droite / haut)
    const right = this.camRight(this.vRight);
    const up = this.camUp(this.vUp);

    switch (p.weapon) {
      case 'lightning': {
        if (!this.lgWasFiring) { this.sound.lightningStart(); this.lgWasFiring = true; }
        p.ammo.lightning--;
        const hit = trace(origin, dir, def.range, this.map.solids, targets, p);
        const muzzle = this.view.muzzleWorld(this.tmpC);
        this.lgBolt.set(muzzle, hit.point);
        this.view.kick('lightning');
        this.fx.muzzleFlash(muzzle, dir, 'lightning', right, up);
        if (hit.target) {
          this.hurtBot(hit.target as Bot, def.damage * quad, 'LG', dir, 1.2);
        } else if (hit.distance < def.range) {
          this.fx.glow(hit.point, 0x9be7ff, 0.22 + Math.random() * 0.1, 0.08, { grow: 1.2 });
          if (Math.random() < 0.35) this.fx.impact(hit.point, hit.normal, 0x9be7ff, 1);
          if (Math.random() < 0.2) this.fx.light(hit.point, 0x9be7ff, 14, 6, 0.09);
        }
        break;
      }
      case 'rocket': {
        p.ammo.rocket--;
        this.sound.rocketFire();
        this.view.kick('rocket');
        const muzzle = this.view.muzzleWorld(this.tmpC).clone();
        this.fx.muzzleFlash(muzzle, dir, 'rocket', right, up);
        this.rockets.push(new Rocket(this.scene, muzzle.addScaledVector(dir, 0.3), dir, 'player', this.fx));
        this.addShake(0.03);
        break;
      }
      case 'rail': {
        p.ammo.rail--;
        this.sound.rail();
        this.view.kick('rail');
        const hit = trace(origin, dir, def.range, this.map.solids, targets, p);
        const muzzle = this.view.muzzleWorld(this.tmpC).clone();
        this.fx.muzzleFlash(muzzle, dir, 'rail', right, up);
        this.fx.railTrail(muzzle, hit.point, def.color);
        if (hit.target) this.hurtBot(hit.target as Bot, def.damage * quad, 'RG', dir, 9);
        else this.fx.impact(hit.point, hit.normal, def.color, 3);
        this.addShake(0.025);
        break;
      }
      case 'shotgun': {
        p.ammo.shotgun--;
        this.sound.shotgun();
        this.sound.casing();
        this.view.kick('shotgun');
        const muzzle = this.view.muzzleWorld(this.tmpC).clone();
        this.fx.muzzleFlash(muzzle, dir, 'shotgun', right, up);
        this.fx.casing(muzzle, right, up);
        const realUp = new THREE.Vector3().crossVectors(right, dir).normalize();
        const hits = new Map<Bot, number>();
        for (let i = 0; i < (def.pellets ?? 1); i++) {
          const d = dir.clone().addScaledVector(right, (Math.random() - 0.5) * 2 * def.spread!).addScaledVector(realUp, (Math.random() - 0.5) * 2 * def.spread!).normalize();
          const hit = trace(origin, d, def.range, this.map.solids, targets, p);
          if (hit.target) hits.set(hit.target as Bot, (hits.get(hit.target as Bot) ?? 0) + def.damage);
          else if (hit.distance < def.range) {
            if (i % 2 === 0) this.fx.tracer(muzzle, d, hit.distance, 0xffe08a);
            if (Math.random() < 0.45) this.fx.impact(hit.point, hit.normal, 0xffe08a, 2);
          }
        }
        for (const [b, dmg] of hits) this.hurtBot(b, dmg * quad, 'SG', dir, 4);
        this.addShake(0.035);
        break;
      }
    }
  }

  // ---------------------------------------------------------------- helpers caméra / secousse

  private camRight(out: THREE.Vector3): THREE.Vector3 {
    return out.set(Math.cos(this.player.mover.yaw), 0, -Math.sin(this.player.mover.yaw));
  }

  private camUp(out: THREE.Vector3): THREE.Vector3 {
    const f = this.player.mover.forward(this.vTmp);
    return out.crossVectors(this.camRight(this.vRight2), f).normalize();
  }

  /** Secousse d'écran (explosions proches, dégâts reçus). */
  addShake(amp: number): void {
    this.shake = Math.min(0.12, this.shake + amp);
  }

  private updateLGBolt(): void {
    const p = this.player;
    const origin = p.mover.eye(this.tmpA);
    const dir = p.mover.forward(this.tmpB);
    const hit = trace(origin, dir, WEAPONS.lightning.range, this.map.solids, this.bots, p);
    this.lgBolt.set(this.view.muzzleWorld(this.tmpC), hit.point);
  }

  private hurtBot(bot: Bot, dmg: number, weapon: string, dir: THREE.Vector3, knock: number): void {
    if (!bot.alive()) return;
    this.sound.hit();
    this.fx.hitFlesh(this.tmpC.copy(bot.mover.position).add(this.vUpUnit), dir, bot.def.accent, dmg);
    // vecteur de recul réutilisé : le LG tape ~20×/s, soit 2 Vector3 par tick
    this.vKnock.copy(dir).multiplyScalar(knock);
    this.vKnock.y += knock * 0.35;
    bot.mover.addVelocity(this.vKnock);
    const killed = bot.damage(dmg);
    if (!killed) this.hud.hitmarker(dmg, false);
    if (killed) {
      this.player.frags++;
      const paid = this.arenaPaid && this.player.frags <= this.arena.maxFrags;
      if (paid) this.player.sats += this.arena.bountySats;
      const bountyNow = this.arenaPaid ? this.arena.bountySats : BOUNTY_SATS;
      const satsMsg = this.arenaPaid
        ? (paid ? `+${this.arena.bountySats} SATS` : 'FRAG BONUS · PLAFOND ATTEINT')
        : `+${BOUNTY_SATS} SATS (DÉMO)`;
      this.hud.kill(this.playerName, bot.def.name, weapon, paid ? this.arena.bountySats : undefined);
      this.hud.frag(`YOU FRAGGED ${bot.def.name.toUpperCase()}`, `${satsMsg} · ${this.playerLegend.killLine?.[0] ?? ''}`);
      this.sound.frag();
      this.reportFrag();
      this.hud.hitmarker(dmg, true);
      this.fx.deathBurst(bot.mover.position.clone().add(new THREE.Vector3(0, 1, 0)), bot.def.accent);
      if (bot.mover.position.distanceTo(this.player.mover.position) < 14) this.addShake(0.05);
      this.publishKill(bot.def.id, weapon);
      if (this.player.frags % 5 === 0) this.sound.announce('excellent');
      if (this.player.frags >= FRAG_LIMIT) this.endMatch();
    }
  }

  private hurtPlayer(dmg: number, from: Bot | null, weapon: string, dir?: THREE.Vector3, knock = 0): void {
    const p = this.player;
    if (!p.alive()) return;
    const absorbed = Math.min(p.armor, Math.floor(dmg * 0.66));
    p.armor -= absorbed;
    p.health -= dmg - absorbed;
    // Les EFFETS (son, flash, secousse, arc) sont limités à ~8/s : le LG des bots
    // tape plusieurs fois par seconde et saturait le HUD. Les dégâts, eux, sont
    // toujours appliqués intégralement.
    const now = performance.now();
    const fxOk = now - this.lastPainAt > 120;
    if (fxOk) {
      this.lastPainAt = now;
      this.hud.damageFlash(Math.min(1, dmg / 40));
      this.sound.pain();
      this.view.kick(0.012);
      this.addShake(0.02 + Math.min(0.03, dmg / 100 * 0.03));
      const hitPoint = this.tmpC.copy(p.mover.position).add(this.vUpUnit);
      this.fx.hitFlesh(hitPoint, dir ?? this.vUpUnit, this.playerLegend.accent, dmg);
    }
    if (from && now - this.lastArcAt > 220) { this.lastArcAt = now; this.hud.damageFrom(this.screenAngleTo(from.mover.position)); }
    if (dir && knock) p.mover.addVelocity(this.vKnock.copy(dir).multiplyScalar(knock));
    if (p.health <= 0) {
      p.alive_ = false;
      p.deaths++;
      this.lgOff();
      this.sound.death();
      this.fx.deathBurst(p.mover.position.clone().add(new THREE.Vector3(0, 1, 0)), this.playerLegend.accent);
      this.addShake(0.08);
      if (from) {
        from.frags++;
        this.hud.kill(from.def.name, this.playerName, weapon);
        this.hud.frag(`${from.def.name.toUpperCase()} FRAGGED YOU`, from.def.killLine[0]);
        if (from.frags >= FRAG_LIMIT) this.endMatch();
      } else if (this.selfSplash) {
        this.hud.frag('SUICIDE — ROCKET JUMP', 'nice trajectory, terrible landing');
      } else {
        this.hud.frag('YOU FELL INTO THE VOID', 'no block reward for you');
      }
      setTimeout(() => { if (!this.over) this.respawnPlayer(); }, RESPAWN_MS);
    }
  }

  private explodeAt(at: THREE.Vector3, ownerId: string): void {
    const R = WEAPONS.rocket.splashRadius!;
    this.fx.explosion(at, R);
    const distToPlayer = at.distanceTo(this.player.mover.position);
    this.sound.explosion(distToPlayer);
    if (distToPlayer < 12) this.addShake(0.1 * (1 - distToPlayer / 12) + 0.02);
    const quad = ownerId === 'player' && performance.now() < this.player.quadUntil ? 3 : 1;
    // Player splash (incl. self → rocket jump)
    const pc = this.player.mover.position.clone().add(new THREE.Vector3(0, 0.9, 0));
    const dp = pc.distanceTo(at);
    if (dp < R && this.player.alive()) {
      const f = 1 - dp / R;
      const dir = pc.sub(at).normalize();
      const dmg = ownerId === 'player' ? Math.round(WEAPONS.rocket.damage * f * 0.5) : Math.round(WEAPONS.rocket.damage * f);
      this.player.mover.addVelocity(dir.multiplyScalar(14 * f));
      this.selfSplash = ownerId === 'player';
      this.hurtPlayer(dmg, ownerId === 'player' ? null : this.bots.find(b => b.id === ownerId) ?? null, 'RL');
      this.selfSplash = false;
    }
    // Bots splash
    for (const b of this.bots) {
      if (!b.alive()) continue;
      const bc = b.mover.position.clone().add(new THREE.Vector3(0, 0.9, 0));
      const d = bc.distanceTo(at);
      if (d < R) {
        const f = 1 - d / R;
        const dir = bc.sub(at).normalize();
        if (ownerId === 'player') this.hurtBot(b, Math.round(WEAPONS.rocket.damage * f * quad), 'RL', dir, 12 * f);
        else if (ownerId !== b.id) { b.mover.addVelocity(dir.multiplyScalar(12 * f)); if (b.damage(Math.round(WEAPONS.rocket.damage * f))) { const k = this.bots.find(x => x.id === ownerId); if (k) { k.frags++; this.hud.kill(k.def.name, b.def.name, 'RL'); } } }
      }
    }
  }

  private botFire(bot: Bot, origin: THREE.Vector3, dir: THREE.Vector3): void {
    // Bots use LG (their signature) with the same rules; Lagarde uses RL sometimes
    const bolt = this.botBolts.get(bot.id)!;
    if (bot.def.id === 'lagarde' && Math.random() < 0.08) {
      this.rockets.push(new Rocket(this.scene, origin.clone().addScaledVector(dir, 0.8), dir, bot.id, this.fx));
      this.fx.muzzleFlash(origin.clone().addScaledVector(dir, 0.8), dir, 'rocket', this.camRight(this.vRight), this.vUpUnit);
      this.sound.rocketFire();
      return;
    }
    const targets: Hittable[] = [this.player, ...this.bots];
    const hit = trace(origin, dir, WEAPONS.lightning.range, this.map.solids, targets, bot);
    // l'origine est déjà le bout du canon du modèle (cf. Bot.muzzleWorld)
    bolt.set(origin, hit.point);
    bot.lastBoltAt = performance.now();
    if (!hit.target && hit.distance < WEAPONS.lightning.range && Math.random() < 0.25) this.fx.impact(hit.point, hit.normal, bot.def.accent, 1);
    if (hit.target === this.player) this.hurtPlayer(WEAPONS.lightning.damage, bot, 'LG', dir, 0.6);
    else if (hit.target && hit.target !== bot) {
      const other = hit.target as Bot;
      if (other.damage(WEAPONS.lightning.damage)) { bot.frags++; this.hud.kill(bot.def.name, other.def.name, 'LG'); if (bot.frags >= FRAG_LIMIT) this.endMatch(); }
    }
  }

  // ---------------------------------------------------------------- world interactions

  private worldTriggers(dt: number): void {
    const p = this.player;
    const now = performance.now();
    // Les bots doivent continuer à utiliser les pads même joueur mort, sinon
    // ils restent bloqués sur le catch deck pendant tout le respawn.
    this.botTriggers(now);
    if (!p.alive()) return;
    const pos = p.mover.position;

    // Void (sécurité — le catch deck rattrape normalement toutes les chutes)
    if (pos.y < this.map.killZ) { p.health = 0; this.hurtPlayer(9999, null, 'VOID'); return; }

    // Catch deck : on ne meurt plus en tombant, on remonte par un pad
    if (pos.y < this.map.deckY + 1) {
      if (!this.onDeck) { this.onDeck = true; this.hud.frag('BACK ON THE DECK', 'PAD UP'); }
    } else if (pos.y > this.map.deckY + 4) this.onDeck = false;

    // Jump pads
    for (const pad of this.map.jumpPads) {
      if (now - pad.lastTrigger > 400 && Math.hypot(pos.x - pad.position.x, pos.z - pad.position.z) < pad.radius && pos.y < pad.position.y + 1.2 && pos.y > pad.position.y - 0.5) {
        pad.lastTrigger = now;
        p.mover.setVelocity(pad.velocity);
        this.sound.jumpPad();
        this.view.kick(0.012);
        this.fx.burst(pad.position, 0xf7931a, 1);
      }
    }
    // Teleporters
    for (const tp of this.map.teleporters) {
      if (Math.hypot(pos.x - tp.position.x, pos.z - tp.position.z) < tp.radius && Math.abs(pos.y - tp.position.y) < 2.5) {
        this.fx.burst(tp.position, 0x9be7ff, 1.2);
        p.mover.teleport(tp.destination, tp.destYaw);
        this.fx.burst(tp.destination, 0x9be7ff, 1.2);
        this.sound.teleport();
        this.hud.damageFlash(0.3);
      }
    }
    // Items — distance au carré sur un temporaire (plus de Vector3 par item)
    const c = this.tmpItem.set(pos.x, pos.y + 0.9, pos.z);
    for (const it of this.map.items) {
      if (it.respawnAt) continue;
      if (it.position.distanceToSquared(c) < 1.69) this.pickup(it);
    }
    // Health decay above 100 (Q3)
    if (p.health > 100) { this.healthStepAcc += dt; if (this.healthStepAcc > 1) { p.health--; this.healthStepAcc = 0; } }
  }

  /** Pads + items des bots — indépendant de l'état du joueur. */
  private botTriggers(now: number): void {
    for (const b of this.bots) {
      if (!b.alive()) continue;
      const bp = b.mover.position;
      const onDeck = bp.y < this.map.deckY + 2;
      for (const pad of this.map.jumpPads) {
        if (Math.hypot(bp.x - pad.position.x, bp.z - pad.position.z) < pad.radius && Math.abs(bp.y - pad.position.y) < 1.2) {
          // sur le deck le bot doit remonter : pas de tirage au sort
          if (onDeck || Math.random() < 0.5) { pad.lastTrigger = now; b.mover.setVelocity(pad.velocity); }
        }
      }
      const bc = this.tmpItem.set(bp.x, bp.y + 0.9, bp.z);
      for (const it of this.map.items) {
        if (!it.respawnAt && it.position.distanceToSquared(bc) < 1.69) {
          if (it.type === 'health') b.health = Math.min(200, b.health + it.amount);
          else if (it.type === 'armor') b.armor = Math.min(200, b.armor + it.amount);
          this.map.takeItem(it, it.type === 'quad' ? 60000 : 25000);
        }
      }
    }
  }

  private pickup(it: ItemSpawn): void {
    const p = this.player;
    switch (it.type) {
      case 'health': if (p.health >= 100 && it.amount <= 25) return; p.health = Math.min(200, p.health + it.amount); break;
      case 'armor': if (p.armor >= 100) return; p.armor = Math.min(200, p.armor + it.amount); break;
      case 'ammo': for (const w of WEAPON_ORDER) p.ammo[w] = Math.min(WEAPONS[w].ammoMax, p.ammo[w] + Math.round(WEAPONS[w].ammoMax * 0.25)); break;
      case 'sats': p.sats += it.amount; break;
      case 'quad': p.quadUntil = performance.now() + 30000; this.hud.frag('QUAD DAMAGE', '30 SECONDS · 3× DAMAGE'); break;
    }
    this.sound.pickup(it.type);
    this.map.takeItem(it, it.type === 'quad' ? 60000 : it.type === 'sats' ? 20000 : 25000);
  }

  // ---------------------------------------------------------------- frame

  private frame(): void {
    const dt = Math.min(0.05, this.clock.getDelta());
    const t = this.clock.elapsedTime;
    this.map.update(t, dt);
    this.fx.update(dt);

    // hauteur de bloc rafraîchie toutes les 60 s
    this.blockTimer += dt;
    if (this.blockTimer > 60) { this.blockTimer = 0; void this.fetchBlockHeight(); }

    if (this.started && !this.over) {
      // ---- Zoom (clic droit / bouton tactile ⊙) : FOV + sensibilité
      const wantZoom = (this.zooming || this.hud.touch.zoom) && this.player.alive();
      this.fovTarget = wantZoom ? 55 : 100;
      if (Math.abs(this.camera.fov - this.fovTarget) > 0.05) {
        // lissage exponentiel : identique à 30, 60 ou 144 fps
        this.camera.fov += (this.fovTarget - this.camera.fov) * (1 - Math.exp(-dt * 11));
        this.camera.updateProjectionMatrix();
      }
      this.hud.setZooming(wantZoom);
      const zoomSens = wantZoom ? 0.55 : 1;

      // ---- Aide à la visée tactile uniquement (friction douce, jamais desktop)
      this.updateAimAssist();
      const assist = this.hud.touch.active ? this.aimAssist : 1;

      // Look
      const look = this.hud.takeLook();
      const sens = 0.0022 * zoomSens;
      const dYaw = this.mouseDX * sens + look.dx * 0.0045 * zoomSens * assist;
      const dPitch = this.mouseDY * sens + look.dy * 0.0045 * zoomSens * assist;
      this.player.mover.yaw -= dYaw;
      this.player.mover.pitch = THREE.MathUtils.clamp(this.player.mover.pitch - dPitch, -1.5, 1.5);
      // Recul caméra : le punch de l'arme monte puis redescend
      const punch = this.view.consumePunch();
      const punchScale = this.hud.touch.active ? 0.25 : 1;
      this.player.mover.pitch = THREE.MathUtils.clamp(this.player.mover.pitch + punch.pitch * punchScale, -1.5, 1.5);
      this.player.mover.yaw += punch.yaw * punchScale;
      // Inertie de l'arme (sway) — suit la souris/le joystick
      this.view.addLook(this.mouseDX * 0.25 + look.dx * 0.5, this.mouseDY * 0.25 + look.dy * 0.5);
      this.mouseDX = 0; this.mouseDY = 0;

      // Move
      if (this.player.alive()) {
        const kb = { f: (this.keys.has('KeyW') || this.keys.has('ArrowUp') ? 1 : 0) - (this.keys.has('KeyS') || this.keys.has('ArrowDown') ? 1 : 0), r: (this.keys.has('KeyD') || this.keys.has('ArrowRight') ? 1 : 0) - (this.keys.has('KeyA') || this.keys.has('ArrowLeft') ? 1 : 0) };
        const input = {
          forward: kb.f || this.hud.touch.moveY,
          right: kb.r || this.hud.touch.moveX,
          jump: this.keys.has('Space') || this.hud.touch.jump,
          crouch: this.keys.has('ControlLeft') || this.keys.has('KeyC'),
        };
        this.player.mover.update(dt, input, this.map);
        // footsteps
        if (this.player.mover.onGround && this.player.mover.speed2D() > 2) {
          this.footstepAcc += dt * this.player.mover.speed2D() * 0.25;
          if (this.footstepAcc > 1) { this.footstepAcc = 0; this.sound.footstep(); }
        }
      }
      // La caméra et l'arme sont recalées AVANT les tirs : sinon `muzzleWorld()`
      // renvoyait la position de la frame précédente (éclair décalé d'une frame).
      this.syncCamera(dt);
      this.worldTriggers(dt);
      this.playerFire(dt);

      // Bots
      this.player.mover.eye(this.tmpLos);
      this.losFrame++;
      resetPlayerFocus();
      // vue joueur + callback LOS réutilisés (une closure et un objet par bot
      // et par frame auparavant)
      const view = this.botPlayerView;
      view.alive = this.player.alive();
      for (const b of this.bots) {
        if (!b.alive()) {
          if (performance.now() > b.respawnAt) b.spawn(this.map, this.aliveAvoids());
          this.botBolts.get(b.id)!.hide();
          continue;
        }
        const fire = b.think(dt, this.map, view, this.bots, this.canSeeFn);
        if (fire) this.botFire(b, fire.origin, fire.dir);
        else if (performance.now() - b.lastBoltAt > 80) this.botBolts.get(b.id)!.hide();
      }

      // Rockets
      // Temporaires réutilisés : la boucle allouait 3 Vector3 et 2 tableaux par
      // roquette et par frame (jusqu'à ~10 roquettes en vol pendant un duel RL).
      const targets = this.rocketTargets;
      for (const r of this.rockets) {
        if (!r.alive) continue;
        const prev = this.rkPrev.copy(r.position);
        r.step(dt, this.fx);
        const d = this.rkDir.subVectors(r.position, prev); const len = d.length(); if (len > 0) d.normalize();
        const armed = r.age > 0.15;
        targets.length = 0;
        if (armed || this.player.id !== r.ownerId) targets.push(this.player);
        for (const b of this.bots) if (armed || b.id !== r.ownerId) targets.push(b);
        const hit = trace(prev, d, len + 0.2, this.map.solids, targets);
        if (hit.distance < len + 0.2 || r.age > 6 || r.position.y < this.map.killZ) {
          const at = hit.distance < len + 0.2
            ? this.rkBoom.copy(hit.point).addScaledVector(hit.normal, 0.15)
            : this.rkBoom.copy(r.position);
          r.dispose(this.scene);
          if (r.age <= 6) this.explodeAt(at, r.ownerId);
        }
      }
      // compactage en place (un tableau neuf par frame avec .filter())
      let alive = 0;
      for (const r of this.rockets) if (r.alive) this.rockets[alive++] = r;
      this.rockets.length = alive;

      // Match clock
      const elapsed = (performance.now() - this.matchStart) / 1000;
      this.hud.setTime(Math.max(0, TIME_LIMIT - elapsed));
      if (elapsed >= TIME_LIMIT) this.endMatch();
      const lead = this.leaderText();
      if (lead !== this.lastLeader) {
        if (lead.startsWith('YOU') && !this.lastLeader.startsWith('YOU') && this.lastLeader) { this.hud.frag('YOU HAVE TAKEN THE LEAD'); this.sound.announce('lead'); }
        else if (!lead.startsWith('YOU') && this.lastLeader.startsWith('YOU')) { this.hud.frag('YOU LOST THE LEAD'); this.sound.announce('lost'); }
        this.lastLeader = lead;
      }
      this.hud.setStats(this.player.health, this.player.armor, this.player.ammo, this.player.weapon, this.player.sats, this.player.frags, lead, this.player.mover.speed2D(), performance.now() < this.player.quadUntil);
      this.hud.setSpread(this.player.mover.speed2D(), !this.player.mover.onGround, dt);
      this.hud.setAimOnEnemy(this.aimOnEnemy);
      this.view.setAmmo(this.player.weapon, this.player.ammo[this.player.weapon]);
    } else if (!this.started) {
      // Menu : orbite lente et basse pour montrer l'arène + les garde-corps
      this.player.mover.yaw = t * 0.06;
      this.player.mover.position.set(Math.sin(t * 0.06 + Math.PI) * 42, 10, Math.cos(t * 0.06 + Math.PI) * 42);
      this.player.mover.pitch = -0.04;
      if (this.camera.fov !== 100) { this.camera.fov = 100; this.camera.updateProjectionMatrix(); }
      this.hud.setZooming(false);
    }

    // Menu (ou match terminé) : la caméra n'a pas encore été synchronisée
    if (!this.started || this.over) this.syncCamera(dt);
    // Écriture DOM du scoreboard seulement sur changement (Tab, frag, mort)
    this.refreshScoreboard();

    if (this.softRaster) this.renderer.render(this.scene, this.camera);
    else this.composer.render();
    this.adaptResolution(dt);
  }

  /**
   * Caméra depuis l'œil du joueur (+ chute de la dead-cam et secousse) puis
   * mise à jour de l'arme vue. Appelé AVANT les tirs pour que `muzzleWorld()`
   * soit à jour dans la frame courante.
   */
  private syncCamera(dt: number): void {
    const eyePos = this.player.mover.eye(this.tmpA);
    if (!this.player.alive()) eyePos.y = this.player.mover.position.y + 0.4;
    this.shake = Math.max(0, this.shake - this.shake * dt * 6 - dt * 0.02);
    const sh = this.shake;
    this.camera.position.copy(eyePos);
    if (sh > 0.0005) this.camera.position.z += (Math.random() - 0.5) * sh * 0.35;
    this.camera.rotation.set(
      this.player.mover.pitch + (Math.random() - 0.5) * sh,
      this.player.mover.yaw + (Math.random() - 0.5) * sh,
      (Math.random() - 0.5) * sh * 0.6,
      'YXZ',
    );
    if (!this.player.alive()) this.camera.rotation.z = 0.35;
    this.camera.updateMatrixWorld(true);
    this.view.update(dt, this.player.mover.speed2D(), this.player.mover.onGround, this.player.alive() && this.started);
  }

  /**
   * Ligne de vue mise en cache : une trace réelle par paire toutes les 3 frames.
   * 5 bots × 5 cibles × 440 solides à chaque frame, c'était le premier poste CPU.
   */
  private canSeeCached(from: THREE.Vector3, to: THREE.Vector3): boolean {
    // Clé numérique : 6 coordonnées quantifiées sur 7 bits (128⁶ < 2⁵³). La clé
    // texte précédente allouait une string par paire, soit ~30 par frame.
    const Q = Game.losQ;
    const key = ((((Q(from.x) * 128 + Q(from.y)) * 128 + Q(from.z)) * 128 + Q(to.x)) * 128 + Q(to.y)) * 128 + Q(to.z);
    const hit = this.losCache.get(key);
    // valeur encodée : frame * 2 + visibilité (pas d'objet par entrée)
    if (hit !== undefined && this.losFrame - Math.floor(hit / 2) < 3) return hit % 2 === 1;
    const d = this.vTmp.subVectors(to, from);
    const len = d.length();
    if (len < 1e-4) return true;
    d.multiplyScalar(1 / len);
    const visible = !traceBlocked(from, d, len - 0.01, this.map.solids);
    if (this.losCache.size > 256) this.losCache.clear();
    this.losCache.set(key, this.losFrame * 2 + (visible ? 1 : 0));
    return visible;
  }

  /** Quantification d'une coordonnée d'arène sur 0..127 (cellules de 1 m). */
  private static losQ(v: number): number {
    const q = Math.round(v) + 64;
    return q < 0 ? 0 : q > 127 ? 127 : q;
  }

  /** Positions à éviter au respawn : joueur + bots vivants. */
  private aliveAvoids(): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    if (this.player.alive()) out.push(this.player.mover.position);
    for (const b of this.bots) if (b.alive()) out.push(b.mover.position);
    return out;
  }

  /**
   * Aide à la visée tactile : friction douce quand un bot est dans un cône de
   * ~6° à moins de 20 m et visible. Aucune assistance au clavier/souris.
   */
  private updateAimAssist(): void {
    const p = this.player;
    const eye = p.mover.eye(this.tmpA);
    const fwd = p.mover.forward(this.tmpB);
    // meilleur candidat par alignement, puis un seul trace (coût constant)
    let best: Bot | null = null; let bestCos = 0.9; let bestDist = 0;
    for (const b of this.bots) {
      if (!b.alive()) continue;
      const d = this.vTmp.subVectors(b.mover.position, p.mover.position);
      const dist = d.length();
      if (dist > 45 || dist < 0.01) continue;
      d.multiplyScalar(1 / dist);
      const cos = d.dot(fwd);
      if (cos > bestCos) { bestCos = cos; best = b; bestDist = dist; }
    }
    let near = false;
    this.aimOnEnemy = false;
    if (best) {
      const hit = trace(eye, fwd, Math.min(45, bestDist + 2.5), this.map.solids, this.bots, p);
      if (hit.target === best) {
        this.aimOnEnemy = true;
        near = bestDist < 20 && bestCos > Math.cos(6 * Math.PI / 180);
      }
    }
    const target = this.hud.touch.active && near ? 0.55 : 1;
    this.aimAssist += (target - this.aimAssist) * 0.15;
  }

  /**
   * Résolution dynamique mobile : au-dessus de 20 ms/frame on descend à 1.25×,
   * sous 14 ms on remonte. Réévalué toutes les 2 s.
   * On mesure `dt` (temps de frame réel, GPU inclus) et non le seul temps CPU :
   * `performance.now() - frameStart` ignorait totalement le coût GPU.
   */
  private adaptResolution(dt: number): void {
    if (!this.hud.touch.active) return;
    this.perfAcc += dt * 1000; this.perfFrames++;
    const now = performance.now();
    if (now - this.perfAt < 2000 || this.perfFrames < 20) return;
    const avg = this.perfAcc / this.perfFrames;
    this.perfAcc = 0; this.perfFrames = 0; this.perfAt = now;
    const cap = Math.min(window.devicePixelRatio, 1.75);
    let next = this.pixelRatioNow;
    if (avg > 20) next = 1.25;
    else if (avg < 14) next = cap;
    if (Math.abs(next - this.pixelRatioNow) < 0.01) return;
    this.pixelRatioNow = next;
    this.renderer.setPixelRatio(next);
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    if (!this.softRaster) {
      const sz = this.renderer.getDrawingBufferSize(new THREE.Vector2());
      this.composer.setSize(sz.x, sz.y);
      (this.fxaa.material.uniforms['resolution'].value as THREE.Vector2).set(1 / sz.x, 1 / sz.y);
    }
  }

  private publishKill(victimId: string, weapon: string): void {
    this.nostr?.publishKill({ matchId: 'lightning-yard-local', killer: this.nostr.getPublicKey(), victim: victimId, weapon, distance: 0, headshot: false, timestamp: Date.now(), bounty: BOUNTY_SATS }).catch(() => {});
  }
}